/**
 * ferrofluidPipeline — spiky ferrofluid patterns.
 *
 * Ferrofluids are liquids that form sharp spikes when exposed to a
 * magnetic field. We simulate this by combining a smooth metaball-like
 * base with directional gradients that create spikes. The spikes
 * orient toward a "magnetic pole" that orbits the screen.
 *
 * The metallic surface has high-contrast specular highlights.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface FerrofluidPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 4; i++) {
    v += a * valueNoise(p);
    p = p * 2.0 + vec2(1.7, 9.2);
    a *= 0.5;
  }
  return v;
}

// 2D rotation matrix.
mat2 rot2(float a) {
  float c = cos(a), s = sin(a);
  return mat2(c, -s, s, c);
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Aspect-corrected screen coords.
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);

  // Magnetic pole position (orbiting).
  float poleAngle = uTime * 0.4;
  vec2 pole = vec2(cos(poleAngle), sin(poleAngle)) * 0.3;

  // Vector from each pixel to the pole.
  vec2 toPole = pole - p;
  float distToPole = length(toPole);
  vec2 dirToPole = toPole / max(distToPole, 0.001);

  // Spike generation: at each pixel, sum radial functions pointing
  // toward the pole. The number and sharpness of spikes depend on
  // audio.
  float spikes = 0.0;
  float spikeCount = 6.0 + bass * 6.0;
  for (float i = 0.0; i < 12.0; i += 1.0) {
    if (i >= spikeCount) break;
    float angle = (i / spikeCount) * 6.2831853;
    // Spike direction: rotated slightly toward the pole.
    vec2 spikeDir = vec2(cos(angle), sin(angle));
    // Bias toward pole.
    spikeDir = normalize(spikeDir * 0.5 + dirToPole * 0.5);
    // Spike position: along spikeDir from center.
    float spikeDist = 0.3;
    vec2 spikePos = spikeDir * spikeDist;
    // Distance from current pixel to spike center.
    float d = length(p - spikePos);
    // Spike width (audio-driven).
    float width = 0.04 + high * 0.02;
    spikes += exp(-d * d / (width * width));
  }

  // Base liquid surface: a metaball-like distance function.
  float liquidBase = exp(-distToPole * 2.0);

  // Combine spikes + base.
  float surface = spikes * 0.8 + liquidBase * 0.4;

  // Ferrofluid surface: dark metallic base with bright highlights.
  vec3 dark = vec3(0.02, 0.04, 0.08);
  vec3 midColor = vec3(0.10, 0.20, 0.40);
  vec3 bright = vec3(0.95, 0.95, 1.0);
  vec3 col = mix(dark, midColor, smoothstep(0.1, 0.5, surface));
  col = mix(col, bright, smoothstep(0.6, 0.95, surface));

  // Specular highlights: bright spots along the spike edges.
  float spec = smoothstep(0.85, 0.95, surface);
  col += vec3(1.0) * spec * 1.5;

  // Subtle iridescent color shift based on noise.
  float noise = fbm(p * 4.0 + vec2(uTime * 0.2, 0.0));
  col = mix(col, col.bgr, noise * 0.3);

  // Background.
  vec3 bg = vec3(0.01, 0.01, 0.03);
  col = mix(bg, col, clamp(length(col), 0.0, 1.0));

  // Vignette.
  float r = length(p);
  float vig = 1.0 - r * r * 0.3;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeFerrofluidBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.6);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.9 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.2 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.4 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.3 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.7 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.2 + 3.0);
}

export function computeFerrofluidRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createFerrofluidPipeline(gl: WebGL2RenderingContext): FerrofluidPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawFerrofluid(
  gl: WebGL2RenderingContext,
  pipe: FerrofluidPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
